import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Close } from '@mui/icons-material'
import { Dialog, DialogContent } from '@dtx-company/design-system/src'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { IconButton, Box as MuiBox } from '@mui/material'
import { PaymentMethod } from '@stripe/stripe-js'
import { PaymentMethodDisplay } from '@app/common/src/components/PlanPurchaseExperience/components/PaymentMethodDisplay'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PrimaryButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { confetti } from '../../utils/confetti'
import { getPlanTypeName } from '../../utils/getPlanTypeName'
import { isOrgPlan } from '@app/common/src/utils/isOrgPlan'
import { theme } from '@dtx-company/shared-components/src'
import { useEffect, useRef } from 'react'
import styled from '@emotion/styled'

const _CloseIconButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  color: ${theme.colors.primary.black};
`

interface UpgradeSuccessModalProps {
  open: boolean
  onClose: () => void
  planType: PlanType | undefined
  paymentMethod: PaymentMethod | undefined
}

function getCTAButtonText(planType: PlanType): string {
  if (isOrgPlan(planType)) {
    return 'Start collaborating'
  }
  return 'Done'
}

export const UpgradeSuccessModal = ({
  open,
  onClose,
  planType = PlanType.PRO,
  paymentMethod
}: UpgradeSuccessModalProps): JSX.Element => {
  const confettiRootRef = useRef<HTMLElement | undefined>(undefined)
  const icon = '/icons/checkmark-filled-white.svg'

  const planTypeName = getPlanTypeName(planType)
  const ctaButtonText = getCTAButtonText(planType)

  useEffect(() => {
    if (!open) return

    let request: number

    function explode(): void {
      // wait for the element to be available
      if (!confettiRootRef.current) {
        request = window.requestAnimationFrame(explode)
        return
      }
      confetti(confettiRootRef.current, {
        angle: 120,
        spread: 296,
        startVelocity: 30,
        elementCount: 340,
        dragFriction: 0.12,
        stagger: 4,
        duration: 7000,
        perspective: '575px'
      })
    }

    request = window.requestAnimationFrame(explode)

    return () => {
      window.cancelAnimationFrame(request)
    }
  }, [open])

  return (
    <>
      <Dialog
        fullWidth={true}
        PaperProps={{
          sx: { maxWidth: 434 }
        }}
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
            onClose()
          }
        }}
      >
        <_CloseIconButton
          aria-label="close"
          onClick={() => {
            onClose()
          }}
        >
          <Close />
        </_CloseIconButton>
        <DialogContent>
          <Box
            data-testid={'update-success-modal-window'}
            flexDirection="column"
            alignItems="center"
            height="100%"
            width="100%"
            maxWidth={['640px', '434px', '434px']}
            m="0 auto"
            bg="primary.white"
            overflowY="auto"
          >
            <Box
              width="100%"
              textAlign="center"
              flexDirection="column"
              alignItems="center"
              zIndex={2}
            >
              <Spacer mb="40px" />
              <Text
                component="h2"
                variant="header/page"
                fontSize="24px"
                fontWeight="600"
                width="100%"
              >
                You're all set!
              </Text>
              <Spacer mb="40px" />
              <Icon width="64px" height="64px" src={icon} />
              <Spacer mb="40px" />
              <Text component="p" variant="body/medium" fontSize="16px" width="100%">
                You've been upgraded to {planTypeName}!
              </Text>
              <Spacer mb="20px" />
              <Text component="p" variant="body/medium" fontSize="16px" width="100%">
                Your order was successfully processed using
              </Text>
              {paymentMethod && (
                <PaymentMethodDisplay paymentMethod={paymentMethod} mode={'short'} />
              )}
              <Spacer mb="40px" />
              <Box width="60%" bottom="5">
                <PrimaryButton onClick={onClose} width="100%">
                  {ctaButtonText}
                </PrimaryButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <MuiBox
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none'
          }}
        >
          <MuiBox
            ref={confettiRootRef}
            style={{
              position: 'absolute',
              top: '40%',
              right: '50%',
              zIndex: 1000
            }}
          />
        </MuiBox>
      </Dialog>
    </>
  )
}
